import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  X, Loader2, IndianRupee, ImagePlus, Wand2, Sparkles,
} from "lucide-react";
import ImageEditor from "@/components/ImageEditor";

const PAPER = ["A4", "A5", "Letter", "Legal", "A3"];
const PAPER_ASPECT = {
  A4: 1 / Math.SQRT2, A5: 1 / Math.SQRT2, A3: 1 / Math.SQRT2,
  Letter: 8.5 / 11, Legal: 8.5 / 14,
};

export default function ImagePrint() {
  const { user, refreshMe } = useAuth();
  const { t } = useI18n();

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSrc, setEditorSrc] = useState(null);
  const [editorName, setEditorName] = useState("photo.jpg");

  // Final image ready for print (post-editor).
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [printers, setPrinters] = useState([]);
  const [form, setForm] = useState({
    copies: 1, color: true, duplex: false, paper_size: "A4",
    orientation: "portrait", fit: "contain", printer_id: "",
  });
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/printers");
      setPrinters(data);
      const def = data.find((p) => p.is_default) || data[0];
      if (def) setForm((f) => ({ ...f, printer_id: def.id }));
    })();
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleFile = (f) => {
    if (!/(jpe?g|png|webp)$/i.test(f.name)) return toast.error("JPG, PNG or WebP only");
    if (f.size > 10 * 1024 * 1024) return toast.error("Max 10 MB");
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditorName(f.name);
      setEditorSrc(e.target.result);
      setEditorOpen(true);
    };
    reader.readAsDataURL(f);
  };

  const onEditorConfirm = (editedFile) => {
    setEditorOpen(false);
    setFile(editedFile);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(editedFile));
    toast.success("Image ready to print");
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(null); setEditorSrc(null);
  };

  const reopenEditor = () => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setEditorName(file.name);
    setEditorSrc(url);
    setEditorOpen(true);
  };

  const submit = async () => {
    if (!file) return toast.error("Upload an image");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      const { data } = await api.post("/orders/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Queued · ₹${data.cost}`);
      await refreshMe();
      clearFile();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const paperAspect = form.orientation === "portrait"
    ? PAPER_ASPECT[form.paper_size]
    : 1 / PAPER_ASPECT[form.paper_size];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="image-print-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Print Studio</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">{t("image.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Upload an image and refine it with the CamScanner-style editor —
          crop, straighten with 4-corner perspective correction, apply filters
          (Auto / Color / B&amp;W / Grayscale) and tune brightness / contrast /
          sharpness. The preview matches the printed page exactly.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3 p-6 border-border bg-card">
          {!file ? (
            <label
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); e.dataTransfer.files?.[0] && handleFile(e.dataTransfer.files[0]); }}
              className={`block cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-all
                ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`}
              data-testid="image-dropzone"
            >
              <div className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <ImagePlus size={24} />
              </div>
              <p className="mt-4 font-display text-lg font-medium">{t("image.dropzone")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("image.hint")}</p>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                data-testid="image-file-input"
              />
            </label>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB · Ready to print</p>
                </div>
                <Button variant="outline" size="sm" onClick={reopenEditor} data-testid="image-reedit-btn">
                  <Wand2 size={13} className="mr-1" /> Re-edit
                </Button>
                <Button variant="ghost" size="icon" onClick={clearFile} data-testid="image-clear-btn">
                  <X size={16} />
                </Button>
              </div>
              <div className="relative rounded-lg border border-border overflow-hidden bg-[repeating-linear-gradient(45deg,#eee_0,#eee_10px,#f7f7f7_10px,#f7f7f7_20px)] dark:bg-neutral-950 max-h-[440px] flex items-center justify-center">
                {previewUrl && (
                  <img src={previewUrl} alt="preview" className="max-h-[440px] max-w-full object-contain" data-testid="image-preview" />
                )}
              </div>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Sparkles size={11} className="text-primary" />
                Preview above is the exact image that will be printed.
              </p>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2 p-6 border-border bg-card space-y-4">
          <h3 className="font-display font-semibold">{t("print.settings")}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("common.copies")}</Label>
              <Input type="number" min={1} value={form.copies}
                onChange={(e) => setForm({ ...form, copies: Math.max(1, parseInt(e.target.value) || 1) })}
                className="mt-1.5" data-testid="image-copies-input" />
            </div>
            <div>
              <Label>{t("common.paper")}</Label>
              <Select value={form.paper_size} onValueChange={(v) => setForm({ ...form, paper_size: v })}>
                <SelectTrigger className="mt-1.5" data-testid="image-paper-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAPER.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("common.orientation")}</Label>
              <Select value={form.orientation} onValueChange={(v) => setForm({ ...form, orientation: v })}>
                <SelectTrigger className="mt-1.5" data-testid="image-orientation-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">{t("common.portrait")}</SelectItem>
                  <SelectItem value="landscape">{t("common.landscape")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("image.fit")}</Label>
              <Select value={form.fit} onValueChange={(v) => setForm({ ...form, fit: v })}>
                <SelectTrigger className="mt-1.5" data-testid="image-fit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contain">{t("image.fitContain")}</SelectItem>
                  <SelectItem value="cover">{t("image.fitCover")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg border border-border">
            <Label>{t("common.color")}</Label>
            <Switch checked={form.color} onCheckedChange={(v) => setForm({ ...form, color: v })} data-testid="image-color-switch" />
          </div>
          <div>
            <Label>{t("orders.printer")}</Label>
            <Select value={form.printer_id} onValueChange={(v) => setForm({ ...form, printer_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="image-printer-select">
                <SelectValue placeholder={printers.length ? "Select" : "No printers"} />
              </SelectTrigger>
              <SelectContent>
                {printers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={submit}
            disabled={submitting || !file || !form.printer_id}
            className="w-full h-11 bg-primary hover:bg-brand-blue-hover"
            data-testid="image-submit-btn"
          >
            {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> {t("print.sending")}</> :
             <><IndianRupee size={14} className="mr-1" /> {t("image.printNow")}</>}
          </Button>
          <p className="text-[11px] text-center text-muted-foreground">
            {t("wallet.currentBalance")}: ₹{Number(user.wallet_balance || 0).toFixed(2)}
          </p>
        </Card>
      </div>

      <ImageEditor
        open={editorOpen}
        src={editorSrc}
        filename={editorName}
        aspect={paperAspect}
        onCancel={() => setEditorOpen(false)}
        onConfirm={onEditorConfirm}
      />
    </div>
  );
}
